import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Link, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Checkbox } from '@cbrebuild/blocks';
import useResizableSplitPane, { getLeaseAbstractionSplitStorageKey, getSplitPaneWidthCssValue } from './useResizableSplitPane';
import FileUploadAccordian, { getVoucherAccordionStorageKey } from '../file-upload/file-upload-accordian';
import LeasePdfViewerPanel from './LeaseInformation/LeasePdfViewerPanel';
import ConsiderationTab from './ConsiderationTab/ConsiderationTab';
import AllocationsTab from './AllocationsTab/AllocationsTab';
import CommissionsTab from './CommissionsTab/CommissionsTab';
import InstallmentsTab from './InstallmentsTab/InstallmentsTab';
import BillToTabContainer from './Bill-To/BillToTabContainer';
import { createLinks } from './BottomNav';
import { subscribeToDealExtractionStatus } from '../../services/attachments-service';
import { dispatchLeaseExtractionRefreshRequest } from '../../hooks/useLeaseExtractionPersistedDataRefresh';
import { createConsideration, deleteConsideration } from '../../redux/actions/considerations-actions';
import {
  fetchAllocationsSection,
  fetchBillToSection,
  fetchCommissionsSection,
  fetchConsiderationSection,
  fetchFinancialBreakdownData,
  fetchInstallmentsSection,
} from '../../redux/actions/financial-actions';
import {
  LEASE_ABSTRACTION_VALIDATION_PAGES,
  isLeaseAbstractionPageValidated,
  setLeaseAbstractionPageValidated,
} from '../../utils/lease-abstraction-validation-storage';
import {
  buildConsiderationFromExtraction,
  getExtractedRentScheduleItems,
} from '../../utils/consideration-term-utils';
import '../file-upload/lease-abstraction.scss';

const FINANCIAL_SECTION_ROUTE_MAP = {
  consideration: 'considerations',
  allocations: 'allocations',
  commissions: 'commissions',
  installments: 'installments',
  'bill-to': 'billTo',
};

const getFinancialAccordionStorageKey = (dealId, sectionKey) => getVoucherAccordionStorageKey(dealId, 'financials', sectionKey);

const getTargetFinancialSection = pathname => FINANCIAL_SECTION_ROUTE_MAP[pathname.split('/').filter(Boolean).pop()];

const FinancialsLeaseAbstractionTab = ({
  deal,
  dealFiles,
  abstractionLeaseFile,
  fetchFiles,
  uploadFile,
}) => {
  const [extractionResults, setExtractionResults] = useState([]);
  const [overrideFile, setOverrideFile] = useState(null);
  const [showMoreInstructions, setShowMoreInstructions] = useState(false);
  const [tAndCFinancials, setTAndCFinancials] = useState(() => isLeaseAbstractionPageValidated(
    deal.id,
    LEASE_ABSTRACTION_VALIDATION_PAGES.financials,
  ));
  const [pdfPageData, setPdfPageData] = useState({ pageNumber: 1, annotationField: 'N/A' });
  const [currentHighlightedField, setCurrentHighlightedField] = useState('N/A'); // eslint-disable-line no-unused-vars
  const requestedPersistedRefreshRef = useRef(null);
  const suppressPersistedRefreshForFileRef = useRef(null);
  const {
    containerRef,
    leftPaneWidth,
    minLeftWidth,
    maxLeftWidth,
    startResize,
    handleDividerKeyDown,
    dividerHintText,
  } = useResizableSplitPane({ storageKey: getLeaseAbstractionSplitStorageKey(deal.id) });

  const dispatch = useDispatch();
  const loadedSectionsRef = useRef({});
  // Persist auto-import flag in sessionStorage so it survives tab switches
  // (component remounts). Without this, hasAutoImportedRef resets to false on
  // every navigation and the auto-import re-runs when the user deleted all rows.
  const autoImportSessionKey = `considerations_auto_imported_${deal.id}`;
  const hasAutoImportedRef = useRef(sessionStorage.getItem(autoImportSessionKey) === 'true');
  const [considerationsFetched, setConsiderationsFetched] = useState(false);
  const [allocationsLoaded, setAllocationsLoaded] = useState(false);
  const existingConsiderations = useSelector(state => state.voucherConsiderations.considerations);

  useEffect(() => {
    loadedSectionsRef.current = {};
    setConsiderationsFetched(false);
    setAllocationsLoaded(false);
  }, [deal.id]);

  // Fetch files on mount if not already loaded (e.g. navigated directly to this tab or page refresh)
  useEffect(() => {
    if (!dealFiles || dealFiles.length === 0) {
      fetchFiles({
        deal: deal.id,
        ordering: '-modified',
        page: 1,
        page_size: 25,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deal.id]);

  const loadSectionOnce = (sectionKey, fetchSection) => {
    if (loadedSectionsRef.current[sectionKey]) return;
    loadedSectionsRef.current[sectionKey] = true;
    fetchSection();
  };

  const loadConsiderations = () => loadSectionOnce('considerations', () => {
    Promise.resolve(dispatch(fetchConsiderationSection(deal.id, deal.deal_type)))
      .then(() => setConsiderationsFetched(true))
      .catch(() => setConsiderationsFetched(true));
  });

  const loadAllocations = () => loadSectionOnce('allocations', () => {
    Promise.resolve(dispatch(fetchAllocationsSection(deal.id)))
      .then(() => setAllocationsLoaded(true))
      .catch(console.error);
  });

  const loadCommissions = () => loadSectionOnce('commissions', () => {
    dispatch(fetchCommissionsSection(deal.id));
  });

  const loadInstallments = () => loadSectionOnce('installments', () => {
    dispatch(fetchInstallmentsSection(deal.id));
  });

  const loadBillTo = () => loadSectionOnce('billTo', () => {
    dispatch(fetchBillToSection(deal.id));
  });

  const loadFinancialBreakdown = () => loadSectionOnce('financialBreakdown', () => {
    dispatch(fetchFinancialBreakdownData(deal.id));
  });

  const routerLocation = useLocation();
  const targetFinancialSection = getTargetFinancialSection(routerLocation.pathname);
  const { dealType, isEarlyInvoice } = useSelector(state => ({
    dealType: state.deal.deal?.deal_type,
    isEarlyInvoice: state.voucher?.voucher?.is_early_invoice,
  }));

  const { prev: abstractionPrev, next: abstractionNext } = createLinks(dealType, routerLocation.pathname, isEarlyInvoice);

  useEffect(() => {
    setTAndCFinancials(isLeaseAbstractionPageValidated(
      deal.id,
      LEASE_ABSTRACTION_VALIDATION_PAGES.financials,
    ));
  }, [deal.id]);

  const handleFinancialsValidationChange = (checked) => {
    setTAndCFinancials(checked);
    setLeaseAbstractionPageValidated(deal.id, LEASE_ABSTRACTION_VALIDATION_PAGES.financials, checked);
  };

  // Resolve the active lease file (same pattern as LeaseInformationTab)
  const resolvedLeaseFile = (() => {
    // Only trust a file reference if it still exists in the current dealFiles list.
    // IMPORTANT: if dealFiles hasn't loaded yet (empty array on refresh) we must NOT
    // treat a stored entry as stale — that would delete it before files arrive.
    const filesLoaded = dealFiles && dealFiles.length > 0;
    const isValid = f => f && f.id && filesLoaded && dealFiles.some(df => df.id === f.id);
    if (isValid(abstractionLeaseFile)) return abstractionLeaseFile;
    try {
      const stored = localStorage.getItem(`abstractionVoucherFile_${deal.id}`);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed && parsed.id) {
          // If files are loaded and the entry is confirmed stale, clear it.
          // Otherwise (files not loaded yet, or file is found) keep / return it.
          if (filesLoaded && !dealFiles.some(df => df.id === parsed.id)) {
            localStorage.removeItem(`abstractionVoucherFile_${deal.id}`);
          } else {
            return parsed;
          }
        }
      }
    } catch (e) { /* ignore */ }
    if (filesLoaded) {
      const leaseFile = dealFiles.find(f => f.is_lease_document === true);
      if (leaseFile) return leaseFile;
      return dealFiles[0];
    }
    return null;
  })();

  // Clear overrideFile if the file it points to no longer exists
  useEffect(() => {
    if (overrideFile && (!dealFiles || !dealFiles.some(f => f.id === overrideFile.id))) {
      setOverrideFile(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealFiles]);

  const activeFile = overrideFile || resolvedLeaseFile;

  // ─── Accept handler: user clicks the date chip to re-import extraction, overwriting existing rows ───
  const handleAcceptExtraction = async () => {
    const rentSchedule = extractionResults[0]?.data_extract?.consideration_info?.rent_schedule;
    if (!rentSchedule) return;
    const extractedItems = getExtractedRentScheduleItems(rentSchedule);
    if (!extractedItems) return;
    // User is explicitly re-importing — reset the session flag so the auto-import
    // guard doesn't block subsequent navigation back to this tab.
    hasAutoImportedRef.current = true;
    sessionStorage.setItem(autoImportSessionKey, 'true');
    await Promise.all(existingConsiderations.map(c => dispatch(deleteConsideration(c.id, { skipFinancialFanout: true }))));
    // Re-import extracted rows sequentially to preserve order
    await extractedItems.reduce(async (chain, ei) => {
      await chain;
      await dispatch(createConsideration(buildConsiderationFromExtraction(ei, deal.id)));
    }, Promise.resolve());
  };

  // ─── Auto-populate considerations from AI extraction ───
  // Only runs after DB fetch is complete AND no rows with real data — prevents re-importing on refresh.
  // Empty/broken placeholder rows (amount=0 AND total_units=0) are treated as non-existent so that
  // a failed prior import attempt or an auto-created empty row does not block auto-population.
  useEffect(() => {
    if (!considerationsFetched) return; // wait until we know what's in DB
    if (hasAutoImportedRef.current) return;
    const hasRealConsiderations = existingConsiderations && existingConsiderations.some(c => Number(c.amount) > 0 || Number(c.total_units) > 0);
    if (hasRealConsiderations) return; // real user-entered rows exist, do not overwrite
    if (!extractionResults || extractionResults.length === 0) return;

    const rentSchedule = extractionResults[0]?.data_extract?.consideration_info?.rent_schedule;
    if (!rentSchedule) return;

    const extractedItems = getExtractedRentScheduleItems(rentSchedule);

    if (!extractedItems) return;

    hasAutoImportedRef.current = true;
    sessionStorage.setItem(autoImportSessionKey, 'true');

    const doImport = async () => {
      // Remove any empty/broken placeholder rows (amount=0 and total_units=0) before importing.
      // These come from a prior failed auto-import or a createIfEmpty pattern in ConsiderationTab.
      const emptyRows = (existingConsiderations || []).filter(c => !Number(c.amount) && !Number(c.total_units));
      if (emptyRows.length > 0) {
        await Promise.all(emptyRows.map(c => dispatch(deleteConsideration(c.id, { skipFinancialFanout: true }))));
      }
      // Create extracted rows sequentially to preserve order
      await extractedItems.reduce(async (chain, ei) => {
        await chain;
        await dispatch(createConsideration(buildConsiderationFromExtraction(ei, deal.id)));
      }, Promise.resolve());
    };
    doImport().catch(() => console.error('Failed to auto-import considerations'));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extractionResults, considerationsFetched]);

  // Subscribe to shared deal-level extraction status updates for the active file.
  useEffect(() => {
    if (!(activeFile && activeFile.id)) {
      setExtractionResults([]);
      return undefined;
    }

    return subscribeToDealExtractionStatus({
      dealId: deal.id,
      fileId: activeFile.id,
      onUpdate: (result) => setExtractionResults(result ? [result] : []),
      onError: () => setExtractionResults([]),
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFile && activeFile.id, deal.id]);

  const extractionResult = extractionResults[0];
  const extractionStatus = extractionResult?.status;

  useEffect(() => {
    if (extractionStatus !== 'Success' || !activeFile?.id) return;
    const refreshKey = `${activeFile.id}:${extractionResult?.id || extractionResult?.status_text || extractionStatus}`;
    if (requestedPersistedRefreshRef.current === refreshKey) return;
    if (suppressPersistedRefreshForFileRef.current === activeFile.id) {
      requestedPersistedRefreshRef.current = refreshKey;
      return;
    }
    requestedPersistedRefreshRef.current = refreshKey;
    dispatchLeaseExtractionRefreshRequest({
      dealId: deal.id,
      fileId: activeFile.id,
      refreshKey,
      extractionResult,
    });
  }, [activeFile, deal.id, extractionResult, extractionStatus]);

  return (
    <div
      className="voucher-lease-abstraction-layout"
      ref={containerRef}
      style={{ '--lease-abstraction-left-pane-width': getSplitPaneWidthCssValue(leftPaneWidth) }}
    >
      {/* LEFT: instructions + 5 accordion sections + footer */}
      <div className="voucher-lease-abstraction-layout__left">

        {/* Instructions banner */}
        <div className="lease-abstraction-instructions">
          <div className="lease-abstraction-instructions__row">
            <p>Instructions</p>
            <button
              type="button"
              className="lease-abstraction-instructions__show-more"
              onClick={() => setShowMoreInstructions(!showMoreInstructions)}
            >
              {showMoreInstructions ? 'Show Less' : 'Show More'}
            </button>
          </div>
          <p className="lease-abstraction-instructions__line1">1. Review the imported data and overwrite fields as needed</p>
          {showMoreInstructions && (
          <div className="lease-abstraction-instructions__extra">
            <ul>
              <li>Click page number to navigate to the area of the document the value was found</li>
              <li>
                If the system detects a different value, underlined{' '}
                <strong style={{ color: 'var(--t-semantic-info-sem-info-text, #5050BB)' }}>purple text</strong> will appear next to
                the field. Click to replace the existing value with the value found by the
                current abstraction.
              </li>
            </ul>
            <p>2. Once you&apos;ve validated the information, check the box at the bottom of
              the page, confirming you have validated the imported data, then select Continue.
            </p>
            <p>3. Repeat the process on each page.</p>
            <p className="lease-abstraction-instructions__fuzzy-note">
              * indicates the page number was derived using fuzzy logic and may be inaccurate
              at times
            </p>
          </div>
            )}
        </div>

        {/* Confidence legend: always visible */}
        <div className="acc-confidence-legend">
          <div className="acc-confidence-badge acc-confidence-badge--high">
            <span className="acc-confidence-badge__icon" aria-hidden="true" />
            <span>High Confidence</span>
          </div>
          <div className="acc-confidence-badge acc-confidence-badge--low">
            <span className="acc-confidence-badge__icon" aria-hidden="true" />
            <span>Low Confidence</span>
          </div>
          <span className="acc-confidence-legend__different">Different Abstracted Value</span>
          <span className="acc-confidence-legend__matching">Matching Abstracted Value</span>
        </div>

        {/* Accordion sections — all 5 financial sections */}
        <div className="lease-abstraction-form-scroll">
          <FileUploadAccordian
            accordianTitle="Considerations"
            forceExpanded={targetFinancialSection === 'considerations'}
            onExpand={loadConsiderations}
            storageKey={getFinancialAccordionStorageKey(deal.id, 'considerations')}
          >
            <ConsiderationTab
              extractionResults={extractionResults}
              onPageRefClick={pageNumber => setPdfPageData({ pageNumber, annotationField: 'N/A' })}
              onAcceptExtraction={handleAcceptExtraction}
            />
          </FileUploadAccordian>

          <FileUploadAccordian
            accordianTitle="Allocations"
            forceExpanded={targetFinancialSection === 'allocations'}
            onExpand={loadAllocations}
            storageKey={getFinancialAccordionStorageKey(deal.id, 'allocations')}
          >
            <AllocationsTab allocationsLoaded={allocationsLoaded} />
          </FileUploadAccordian>

          <FileUploadAccordian
            accordianTitle="Commissions"
            forceExpanded={targetFinancialSection === 'commissions'}
            onExpand={loadCommissions}
            storageKey={getFinancialAccordionStorageKey(deal.id, 'commissions')}
          >
            <CommissionsTab />
          </FileUploadAccordian>

          <FileUploadAccordian
            accordianTitle="Installments"
            forceExpanded={targetFinancialSection === 'installments'}
            onExpand={loadInstallments}
            storageKey={getFinancialAccordionStorageKey(deal.id, 'installments')}
          >
            <InstallmentsTab />
          </FileUploadAccordian>

          <FileUploadAccordian
            accordianTitle="Bill To"
            forceExpanded={targetFinancialSection === 'billTo'}
            onExpand={loadBillTo}
            storageKey={getFinancialAccordionStorageKey(deal.id, 'billTo')}
          >
            <BillToTabContainer />
          </FileUploadAccordian>
        </div>

        {/* Footer: T&C checkbox + Back/Continue */}
        <div className="lease-abstraction-footer">
          {activeFile && (
            <div className="lease-abstraction-footer__checkbox">
              <Checkbox
                checked={tAndCFinancials}
                onChange={({ target: { checked } }) => handleFinancialsValidationChange(checked)}
              >
                I have validated the data being imported
              </Checkbox>
            </div>
          )}
          <div className="lease-abstraction-footer__actions">
            {abstractionPrev && (
              <Link to={abstractionPrev.url} className="cancel-btn">BACK</Link>
            )}
            {abstractionNext && (
              <Link
                to={(!activeFile || tAndCFinancials) ? abstractionNext.url : '#'}
                className={`upload-btn${(activeFile && !tAndCFinancials) ? ' disabled' : ''}`}
                onClick={(e) => { if (activeFile && !tAndCFinancials) e.preventDefault(); }}
              >
                CONTINUE
              </Link>
            )}
          </div>
        </div>

        {/* Disclaimer: only when extraction file present */}
        {activeFile && (
          <div className="lease-abstraction-disclaimer">
            <p className="lease-abstraction-disclaimer__title">Disclaimer</p>
            <p>
              The information contained herein includes data that has been extracted using
              artificial intelligence technology. While the technology has been tested for accuracy
              and reliability, the data in each field have not been individually verified for
              accuracy or reliability. CBRE, Inc. makes no guarantee, representation or warranty
              and accepts no responsibility or liability as to the accuracy, completeness, or
              reliability of the extracted data contained herein. You should conduct a careful,
              individual investigation of all information. Any reliance on this material is solely
              at your own risk.
            </p>
          </div>
        )}
      </div>

      <div
        className="voucher-lease-abstraction-layout__divider"
        role="separator"
        tabIndex={0}
        aria-label="Resize form and PDF panels"
        aria-orientation="vertical"
        aria-valuemin={minLeftWidth}
        aria-valuemax={maxLeftWidth}
        aria-valuenow={Math.round(leftPaneWidth)}
        onMouseDown={startResize}
        onTouchStart={startResize}
        onKeyDown={handleDividerKeyDown}
        title={dividerHintText}
        style={{ '--split-pane-hint-text': `'${dividerHintText}'` }}
      />
      {/* RIGHT: PDF viewer panel */}
      <div className="voucher-lease-abstraction-layout__right">
        <LeasePdfViewerPanel
          dealFiles={dealFiles || []}
          initialFile={activeFile || null}
          onFileChange={(file) => {
            suppressPersistedRefreshForFileRef.current = file?.id || null;
            setOverrideFile(file);
            try { localStorage.setItem(`abstractionVoucherFile_${deal.id}`, JSON.stringify(file)); } catch (e) { /* ignore */ }
          }}
          onFileUploaded={(file) => {
            suppressPersistedRefreshForFileRef.current = null;
            setOverrideFile(file);
            try { localStorage.setItem(`abstractionVoucherFile_${deal.id}`, JSON.stringify(file)); } catch (e) { /* ignore */ }
          }}
          uploadFile={uploadFile}
          dealId={deal.id}
          extractionResults={extractionResults}
          pdfPageData={pdfPageData}
          setPdfPageData={setPdfPageData}
          setCurrentHighlightedField={setCurrentHighlightedField}
          defaultActiveTab="comments"
          onFinancialCommentsTabSelected={loadFinancialBreakdown}
          tabStorageContext="financials-lease-comp"
        />
      </div>
    </div>
  );
};

FinancialsLeaseAbstractionTab.propTypes = {
  deal: PropTypes.shape({
    id: PropTypes.number.isRequired,
    deal_type: PropTypes.string,
  }).isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  dealFiles: PropTypes.arrayOf(PropTypes.object),
  // eslint-disable-next-line react/forbid-prop-types
  abstractionLeaseFile: PropTypes.object,
  fetchFiles: PropTypes.func,
  uploadFile: PropTypes.func,
};

FinancialsLeaseAbstractionTab.defaultProps = {
  dealFiles: [],
  abstractionLeaseFile: null,
  fetchFiles: () => {},
  uploadFile: () => {},
};

export default FinancialsLeaseAbstractionTab;
